import Layout from "../../../components/Layout";
import { Link, useLocation } from "react-router-dom";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import Chip from "../../../components/Chip";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import FilterSellOrders from "../../../api/sell-orders/filterSellOrders";
import {
  DeleteIcon,
  DownloadIcon,
  EyeOpen,
  PrintIcon,
} from "../../../assets/icons/Icons";
import SearchFilter from "../../../components/filters/SearchFilter";
import ViewSellOrder from "../../../components/sell-order/View";
import { formatDate } from "../../../utils/formatDate";
import ResetFilters from "../../../components/filters/ResetFilters";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../../../components/CustomTooltip";
import ConfirmModal from "../../../components/ConfirmModal";
import { DeleteSellOrder } from "../../../api/sell-orders/deleteSellOrder";
import { getReceipt } from "../../../api/sell-orders/getReceipt";
import { getBatchReceipt } from "../../../api/batch-order/getBatchReceipt";
import { getCombineCardReceipt } from "../../../api/sell-orders/getCombineCardReceipt";
import { getReplaceCardReceipt } from "../../../api/sell-orders/getReplaceCardReceipt";
import usePrint from "../../../hooks/usePrint";
import { CentsToDollars } from "../../../utils/formatPrice";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { useEffect } from "react";
import ViewBatchSellOrder from "../../../components/batch-order/View";
import ViewCombineOrder from "../../../components/sell-order/ViewCombineOrder";
import ViewReplaceOrder from "../../../components/sell-order/ViewReplaceOrder";
import GiftReceipt from "../../../components/GiftReceipt";

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    pageCount,
    errorMessage,
    loadingData,
    selectedItem,
    setSelectedItem,
    showViewItem,
    setShowViewItem,
    selectedForDelete,
    setSelectedForDelete,
    showDeleteItem,
    setShowDeleteItem,
    deleteItem,
    deleteErrorMessage,
    setdeleteErrorMessage,
    setdeleteLoading,
    deleteLoading,
  } = useData(FilterSellOrders, DeleteSellOrder);

  const { handlePrint: handlePrintReceipt } = usePrint(getReceipt);
  const { handlePrint: handlePrintBatchReceipt } = usePrint(getBatchReceipt);
  const { handlePrint: handlePrintCombineReceipt } = usePrint(getCombineCardReceipt);
  const { handlePrint: handlePrintReplaceReceipt } = usePrint(getReplaceCardReceipt);

  // Export
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryParams = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  const prn = queryParams?.prn;

  const renderSellerOrder = (orders) => {
    return prn && orders?.length === 1 && !loadingData ? orders?.map((o) => ({ ...o, prn: prn ?? "" })) : [];
  };

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Card Issuing"));
    setBreadcrumbs([t("Card Issuing"), t("Search Orders by Proxy")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Layout title={t("Search By Proxy")} breadcrumbs={t("Card Issuing")}>
      {showViewItem && (
        <>
        {(() => {
          const modalProps = {
            order: selectedItem,
            onClose: () => setShowViewItem(false),
          };
          switch (selectedItem?.type) {
            case "batch":
              return <ViewBatchSellOrder {...modalProps} />;
            case "combine":
              return <ViewCombineOrder {...modalProps} />;
            case "replace":
              return <ViewReplaceOrder {...modalProps} />;
            default:
              return <ViewSellOrder {...modalProps} />;
          }
        })()}
        </>
      )}

      {showDeleteItem && (
        <ConfirmModal
          title={
            t("Sell Order With ID ") + selectedForDelete?.id + t(" will be deleted")
          }
          question={t("Please confirm order deletion")}
          onClose={(e) => {
            setShowDeleteItem(false);
            setSelectedForDelete(null);
          }}
          onConfirm={deleteItem}
          selectedForDelete={selectedForDelete}
          setSelectedForDelete={setSelectedForDelete}
          isOfficeRequired={true}
          deleteLoading={deleteLoading}
          deleteErrorMessage={deleteErrorMessage}
          setdeleteErrorMessage={setdeleteErrorMessage}
          setdeleteLoading={setdeleteLoading}
        />
      )}

      <DataTable
        filtersTop={
          <>
            <FilterContextProvider name="prn">
              <SearchFilter placeholderText={t("Search By Proxy")} />
              {prn && <ResetFilters text={"Reset"} />}
            </FilterContextProvider>
          </>
        }
        filtersBottom={<></>}
        buttons={<></>}
        columns={[
          { header: t("Card Proxy"), accessor: "prn" },
          { header: t("Order Number"), accessor: "id" },
          { header: t("Date"), accessor: "created_at" },
          { header: t("Order Type"), accessor: "type" },
          { header: t("Order Status"), accessor: "status" },
          { header: t("User"), accessor: "created_by_name" },
          { header: t("Total Purchase Amount"), accessor: "total_cost" },
          { header: t("Office Name"), accessor: "office_name" },
          { header: t("Actions"), accessor: "actions" },
        ]}
        rows={renderSellerOrder(data?.data || [])}
        pageCount={pageCount}
        loadingData={prn ? loadingData : false}
        error={errorMessage}
        emptyDataMessage={t(
          queryParams?.prn ? "PRN doesn’t exist" : "Search by valid PRN"
        )}
        actions={(item) => {
          const printHandlers = {
            regular: handlePrintReceipt,
            batch: handlePrintBatchReceipt,
            combine: handlePrintCombineReceipt,
            replace: handlePrintReplaceReceipt,
          };
          const handlePrint = printHandlers[item.type] || handlePrintReceipt;
          const receiptName = item.type === "combine" ? `combine-order-${item.id}`
            : item.type === "replace" ? `replacement-order-${item.id}`
            : `order-${item.id}`;
          const receiptStatus = item.type === "replace" ? "success" : "completed";
          const hasVoid = item.type === "regular" || item.type === "batch" || !item.type;
          const hasGiftReceipt = item.type === "regular" || item.type === "batch" || !item.type;

          return (
            <div className="d-flex align-items-center">
              {item.status === receiptStatus && (
                <>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePrint({
                        requestParams: { id: item.id },
                        name: receiptName,
                        width: "",
                        height: "",
                        download: false,
                      });
                    }}
                  >
                    <CustomTooltip
                      icon={<PrintIcon />}
                      message={t("Print Receipt")}
                    />
                  </Link>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePrint({
                        requestParams: { id: item.id },
                        name: receiptName,
                        width: "",
                        height: "",
                        download: true,
                      });
                    }}
                  >
                    <CustomTooltip
                      icon={<DownloadIcon />}
                      message={t("Get Receipt")}
                    />
                  </Link>
                  {hasGiftReceipt && <GiftReceipt order={item} />}
                </>
              )}
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedItem(item);
                  setShowViewItem(true);
                }}
              >
                <CustomTooltip icon={<EyeOpen />} message={t("View")} />
              </Link>
              {hasVoid && (
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowDeleteItem(true);
                    setSelectedForDelete({ ...selectedForDelete, id: item.id });
                  }}
                >
                  <CustomTooltip icon={<DeleteIcon />} message={t("Void")} />
                </Link>
              )}
            </div>
          );
        }}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "status":
              let status = "inactive";
              if (
                value === "submitted" ||
                value === "success" ||
                value === "completed"
              ) {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "created_at":
              return formatDate(value);
            case "total_cost":
              return <p className="text-right">{CentsToDollars(value)}</p>;
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
